import { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { Card, Box, Stack, Chip, Link, Collapse, Typography, ButtonBase } from '@mui/material';
// utils
import { TRANSFERRED_COUNT_LABELS, humaniseMatchReason, leadDetailPath } from '../../../utils/mergeHistoryDisplay';

// ----------------------------------------------------------------------
// Merge history — the audit trail for identity merges involving this lead.
// Rendered only when the lead was actually part of a merge, so the Overview
// layout is untouched for every other lead.
// ----------------------------------------------------------------------

const FIELD_DECISION_LABELS = {
    primary: 'kept primary',
    secondary: 'used secondary',
};

const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const actorName = (actor) => {
    const name = `${actor?.first_name || ''} ${actor?.last_name || ''}`.trim();
    return name || 'system';
};

const countChips = (counts) =>
    Object.entries(counts || {})
        // Zero counts are noise in an audit summary — omit them entirely.
        .filter(([, value]) => Number(value) > 0)
        .map(([key, value]) => ({
            key,
            label: `${TRANSFERRED_COUNT_LABELS[key] || humaniseMatchReason(key)}: ${value}`,
        }));

// ----------------------------------------------------------------------

const MergeHistoryCard = ({ leadDetail, practiceId }) => {
    const [expanded, setExpanded] = useState(true);
    // Unique per instance so two MergeHistoryCard cards on the same page
    // can't collide on the same DOM id.
    const contentId = useId();

    const mergeHistory = leadDetail?.merge_history ?? { as_primary: [], as_secondary: [] };
    const asPrimary = mergeHistory.as_primary ?? [];
    const asSecondary = mergeHistory.as_secondary ?? [];

    if (!asPrimary.length && !asSecondary.length) return null;

    const resolvedPracticeId = practiceId ?? leadDetail?.practice_id ?? leadDetail?.practice;
    const snapshots = leadDetail?.lead_source?.metadata?.attribution_history ?? [];

    return (
        <Card sx={{ p: 2, mt: 2 }}>
            <ButtonBase
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                aria-controls={contentId}
                sx={{ width: '100%', justifyContent: 'space-between', p: 0.5, borderRadius: '8px' }}
            >
                <Typography variant='subtitle1'>Merge history</Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                    {`${asPrimary.length + asSecondary.length} ${
                        asPrimary.length + asSecondary.length === 1 ? 'record' : 'records'
                    }`}
                </Typography>
            </ButtonBase>

            <Collapse in={expanded} unmountOnExit id={contentId}>
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    {asPrimary.map((entry) => {
                        const chips = countChips(entry.transferred_counts);
                        // The backend stores operator decisions as `field: 'primary'|'secondary'`
                        // and, under `unresolved_conflicts`, an object of the fields that
                        // differed but were left undecided (primary value kept). Render the
                        // two shapes separately instead of stringifying the object.
                        const { unresolved_conflicts: unresolvedConflicts, ...rawDecisions } = entry.field_decisions || {};
                        const decisions = Object.entries(rawDecisions).filter(([, decision]) => typeof decision === 'string');
                        const unresolvedFields = Object.keys(unresolvedConflicts || {});
                        return (
                            <Box
                                key={`primary-${entry.id}`}
                                data-testid={`merge-as-primary-${entry.id}`}
                                sx={{ p: 1.5, borderRadius: '10px', border: '1px solid', borderColor: 'divider' }}
                            >
                                <Typography variant='body2'>
                                    {`Merged from lead #${entry.secondary_lead_id} (${
                                        entry.secondary_lead_name || 'Unknown'
                                    }) on ${formatDate(entry.created_at)} by ${actorName(entry.actor)}`}
                                </Typography>

                                {entry.match_reason && (
                                    <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                                        {humaniseMatchReason(entry.match_reason)}
                                    </Typography>
                                )}

                                {chips.length > 0 && (
                                    <Stack direction='row' spacing={1} flexWrap='wrap' sx={{ mt: 1 }}>
                                        {chips.map((chip) => (
                                            <Chip
                                                key={chip.key}
                                                size='small'
                                                label={chip.label}
                                                aria-label={`Transferred ${chip.label}`}
                                            />
                                        ))}
                                    </Stack>
                                )}

                                {decisions.length > 0 && (
                                    <Box component='ul' aria-label='Field decisions' sx={{ listStyle: 'none', m: '8px 0 0', p: 0 }}>
                                        {decisions.map(([field, decision]) => (
                                            <Box component='li' key={field}>
                                                <Typography variant='caption'>
                                                    {`${field}: ${FIELD_DECISION_LABELS[decision] || humaniseMatchReason(decision)}`}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {unresolvedFields.length > 0 && (
                                    <Typography variant='caption' color='text.secondary' component='p' sx={{ mt: 0.5 }}>
                                        {`Left unresolved (primary kept): ${unresolvedFields.join(', ')}`}
                                    </Typography>
                                )}

                                {entry.secondary_lead_id !== undefined && entry.secondary_lead_id !== null && (
                                    resolvedPracticeId !== undefined && resolvedPracticeId !== null ? (
                                        <Link
                                            component={RouterLink}
                                            to={leadDetailPath(resolvedPracticeId, entry.secondary_lead_id)}
                                            variant='body2'
                                            sx={{ display: 'inline-block', mt: 1 }}
                                        >
                                            View archived lead
                                        </Link>
                                    ) : (
                                        <Typography variant='body2' sx={{ display: 'inline-block', mt: 1 }}>
                                            View archived lead
                                        </Typography>
                                    )
                                )}
                            </Box>
                        );
                    })}

                    {asSecondary.map((entry) => (
                        <Box
                            key={`secondary-${entry.id}`}
                            data-testid={`merge-as-secondary-${entry.id}`}
                            sx={{ p: 1.5, borderRadius: '10px', border: '1px solid', borderColor: 'divider' }}
                        >
                            <Typography variant='body2'>
                                {`This lead was merged into lead #${entry.primary_lead_id} (${
                                    entry.primary_lead_name || 'Unknown'
                                }) on ${formatDate(entry.created_at)}`}
                            </Typography>

                            {entry.match_reason && (
                                <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                                    {humaniseMatchReason(entry.match_reason)}
                                </Typography>
                            )}

                            {entry.primary_lead_id !== undefined && entry.primary_lead_id !== null && (
                                resolvedPracticeId !== undefined && resolvedPracticeId !== null ? (
                                    <Link
                                        component={RouterLink}
                                        to={leadDetailPath(resolvedPracticeId, entry.primary_lead_id)}
                                        variant='body2'
                                        sx={{ display: 'inline-block', mt: 1 }}
                                    >
                                        Open surviving lead
                                    </Link>
                                ) : (
                                    <Typography variant='body2' sx={{ display: 'inline-block', mt: 1 }}>
                                        Open surviving lead
                                    </Typography>
                                )
                            )}
                        </Box>
                    ))}

                    {snapshots.length > 0 && (
                        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                            {`Attribution history preserved (${snapshots.length} snapshot${
                                snapshots.length === 1 ? '' : 's'
                            })`}
                        </Typography>
                    )}
                </Stack>
            </Collapse>
        </Card>
    );
};

MergeHistoryCard.propTypes = {
    leadDetail: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

MergeHistoryCard.defaultProps = {
    leadDetail: null,
    practiceId: undefined,
};

export default MergeHistoryCard;
